import { useState } from 'react';

import styled from 'styled-components';

import { createForm } from '@components/form';
import { IconButton } from '@components/icon-button';
import { IconApplicants } from '@components/icons/applicants';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconDirectionRight } from '@components/icons/direction-right';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { IApplication } from 'models/types';

import { H3BulkEditField } from './api';
import { useH3BulkCreateData, useH3CoreBulkEditData } from './hooks';

const Form = createForm();

export const useBulkEditH3CoreSidebar = () => useSidebar(BulkEditH3Core);

type InputFieldProps = {
  field: H3BulkEditField;
};

function InputField({ field }: InputFieldProps) {
  function getOptions(choices: string[][]) {
    return choices.map((choice) => {
      return { value: String(choice[0]), label: choice[1] };
    });
  }

  switch (field.type) {
    case 'string':
      return <Form.TextField label={field.label} id={field.name} validators={field.validators} />;
    case 'choices':
    case 'autocomplete':
      return (
        <Form.Select
          label={field.label}
          id={field.name}
          options={getOptions(field.choices)}
          validators={field.validators}
        />
      );
    case 'date':
      return <Form.DatePicker label={field.label} id={field.name} validators={field.validators} />;
    default:
      return null;
  }
}

const SidebarContainer = styled(Stack).attrs({
  space: 6,
})({
  paddingBottom: '2.5rem',
});

type BulkEditCoreProps = {
  applicants: IApplication[];
};

export const BulkEditH3Core = (props: BulkEditCoreProps) => {
  const { t } = useScopedTranslate(locale);
  const [isOpen, setIsOpen] = useState(false);
  const dataQuery = useH3CoreBulkEditData();
  const createMutation = useH3BulkCreateData();
  if (dataQuery.isLoading) return <RelativeSpinner />;
  if (dataQuery.isError) return <div>Loading or error...</div>;

  const onSuccess = async () => {
    hideSidebar();
  };

  const handleSubmit = (values: H3BulkEditField[]) => {
    const data = {
      applicationIds: props.applicants.map((applicant) => applicant.id),
      fields: values,
      onSuccess,
    };
    createMutation.mutate(data);
  };

  const fields = dataQuery.data;

  if (props.applicants.length === 0) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader title={t('bulkEditH3')} />
        <Sidebar.Content>
          <Text text={t('noValidApplicants')} />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('bulkEditH3')} />
      <Sidebar.Content>
        <SidebarContainer>
          <Stack space={4}>
            <Text text={t('editInfoDescription')} font="regular14" color="purple80" />
            <Stack orientation="horizontal" justify="space-between">
              <Stack space={2} orientation="horizontal" align="center">
                <IconApplicants color="purple50" />
                <Text
                  text={t('selectedApplicants', props.applicants.length.toString())}
                  font="bold14"
                />
              </Stack>
              {isOpen ? (
                <IconButton
                  onClick={() => setIsOpen(false)}
                  icon={<IconDirectionDown />}
                  variant="ghost-purple"
                />
              ) : (
                <IconButton
                  onClick={() => setIsOpen(true)}
                  icon={<IconDirectionRight />}
                  variant="ghost-purple"
                />
              )}
            </Stack>
            {isOpen ? (
              <Stack>
                {props.applicants.map((applicant) => (
                  <Text font="bold14" text={applicant.name} key={applicant.id} />
                ))}
              </Stack>
            ) : null}
          </Stack>
          <Separator />
          <Form
            onSubmit={(values: H3BulkEditField[]) => {
              handleSubmit(values);
            }}
          >
            {fields.map((data: any, i: number) => (
              <InputField field={data} key={i} />
            ))}
            <Form.Actions>
              <Form.Reset text={t('cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit text={t('save')} variant="primary-blue" />
            </Form.Actions>
          </Form>
        </SidebarContainer>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    bulkEditH3: 'Bulk edit H3',
    editInfoDescription: 'Edit H3 field information for selected applicants',
    save: 'Save',
    cancel: 'Cancel',
    selectedApplicants: 'Selected applicant(s) • {0}',
    noValidApplicants:
      'There are no valid applicants in the selection you have made. Please make sure the applications are marked as hired and have not already been created in H3',
  },
  is: {
    bulkEditH3: 'Breyta H3 gildum',
    editInfoDescription: 'Uppfæra H3 upplýsingar fyrir valdnar umsóknir',
    save: 'Vista',
    cancel: 'Hætta við',
    selectedApplicants: 'Valdnar umsóknir • {0}',
    noValidApplicants:
      'Engar gildar umsóknir hafa verið valdnar. Vinsamlegast athugið að einungis umsóknir sem hafa verið merktar ráðnar og hafa ekki nú þegar verið stofnaðir í H3 eru gildar í þessa aðgerð',
  },
};
